// Lightweight local persistence so an interrupted wizard can resume and the
// last report stays reachable. Deliberately minimal: only ids, the step
// number, and timestamps are stored — answers, photos, and contact details
// live exclusively with the server-side session (which auto-expires), so
// nothing here extends the promised data lifetime. All best-effort: private
// browsing or storage errors silently degrade to the old in-memory behavior.

const WIZARD_KEY = "fscm.wizard";
const REPORT_KEY = "fscm.lastReport";

// Mirrors the default RETENTION_DAYS — a saved pointer past this is dead.
const WIZARD_TTL_MS = 60 * 24 * 60 * 60 * 1000;

export interface SavedWizard {
  sessionId: string;
  step: number;
  savedAt: number; // epoch ms
  color?: string; // garage car color (visual only)
}

export interface SavedReport {
  reportId: string;
  expiresAt: string; // ISO, from the server (capped at the session's expiry)
}

function read<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function write(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* best-effort */
  }
}

function remove(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    /* best-effort */
  }
}

/** Validated + TTL-gated: corrupt or stale entries are removed, not returned. */
export function loadWizard(): SavedWizard | null {
  const v = read<SavedWizard>(WIZARD_KEY);
  if (
    !v ||
    typeof v.sessionId !== "string" ||
    !v.sessionId ||
    !Number.isInteger(v.step) ||
    v.step < 0 ||
    v.step > 20 ||
    typeof v.savedAt !== "number" ||
    Date.now() - v.savedAt > WIZARD_TTL_MS
  ) {
    if (v !== null) remove(WIZARD_KEY);
    return null;
  }
  if (v.color !== undefined && !/^#[0-9a-f]{6}$/i.test(String(v.color))) delete v.color;
  return v;
}

export const saveWizard = (v: Omit<SavedWizard, "savedAt">) =>
  write(WIZARD_KEY, { ...v, savedAt: Date.now() });

export const clearWizard = () => remove(WIZARD_KEY);

export function loadReport(): SavedReport | null {
  const v = read<SavedReport>(REPORT_KEY);
  if (
    !v ||
    typeof v.reportId !== "string" ||
    !v.reportId ||
    typeof v.expiresAt !== "string" ||
    Number.isNaN(Date.parse(v.expiresAt))
  ) {
    if (v !== null) remove(REPORT_KEY);
    return null;
  }
  return v;
}

/** Returns whether the write verifiably stuck (false in private browsing etc.). */
export function saveReport(v: SavedReport): boolean {
  write(REPORT_KEY, v);
  return read<SavedReport>(REPORT_KEY)?.reportId === v.reportId;
}

/** Reports expire server-side; don't offer dead links. */
export const reportLooksAlive = (r: SavedReport): boolean =>
  Date.now() < Date.parse(r.expiresAt);
